import {getLocalStorageItem, setLocalStorageItem} from '@gaia/utils/localStorage';
import identity from 'lodash/identity';
import {useEffect, useRef, useState} from 'react';

/*
 * usePersistentState manages React component state that is also synced to Local Storage.
 *
 *   key: The key used to stored and retrieve the state from Local Storage. Assumed to be unique
 *     and immutable: this hook does not support changing the key.
 *
 *   defaultValue: Value that should be used if nothing is found in Local Storage.
 *
 *   options:
 *      onChange: Fires when the state changes.
 *      transformLocalStorage: Transforms the value retrieved from Local Storage. Useful if the
 *        object type has been expanded and new fields need to be added to the stored value.
 */
type Options<T> = {
    transformLocalStorage?: (state: T) => T;
    onChange?: (state: T) => void;
};

export default function usePersistentState<T>(
    key: string,
    defaultValue: T,
    {transformLocalStorage = identity, onChange}: Options<T> = {
        transformLocalStorage: identity,
    }
): [T, SetState<T>] {
    const [state, setState] = useState<T>(() =>
        transformLocalStorage(getLocalStorageItem<T>(key, defaultValue))
    );

    // key must not change
    const keyRef = useRef(key);

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        onChangeRef.current?.(state);
        setLocalStorageItem(keyRef.current, state);
    }, [state]);

    return [state, setState];
}
